import { useEffect, useMemo, useState } from "react";
import { Banknote, Copy, ImagePlus, Minus, Package, Plus, Receipt, RotateCcw, Search, ShoppingCart, Trash2, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import PinConfirm from "../components/PinConfirm";
import { ImageLightbox } from "../components/ImageLightbox";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const TYPE_LABEL = { stok_alat: "Order Stok Alat", buka_cabang: "Buka Cabang Baru" };
const STATUS_STYLE = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  diterima: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
  diproses: "bg-sky-500/10 text-sky-500 border-sky-500/20",
  dikirim: "bg-violet-500/10 text-violet-500 border-violet-500/20",
  selesai: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  ditolak: "bg-rose-500/10 text-rose-500 border-rose-500/20",
};
const STATUS_LABEL = { pending: "Pending", diterima: "Diterima", diproses: "Diproses", dikirim: "Dikirim", selesai: "Selesai", ditolak: "Ditolak" };


export default function PesanAlat() {
  const { user: me } = useAuth();
  const [BANK_ACCOUNTS, setBankAccounts] = useState([]);
  useEffect(() => { api.get("/bank-accounts", { params: { scope: "deposit" } }).then((r) => setBankAccounts(r.data)).catch(() => {}); }, []);
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("belanja");
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("ALL");
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [payBank, setPayBank] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [ongkirFor, setOngkirFor] = useState(null);
  const [ongkirVal, setOngkirVal] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pinStatusOpen, setPinStatusOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const API_BASE = api.defaults.baseURL;

  const load = async () => {
    try { const r = await api.get("/tool-requests"); setList(r.data); return r.data; } catch { return []; }
  };
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const categories = useMemo(() => ["ALL", ...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => (catFilter === "ALL" || p.category === catFilter)
      && (!q || p.name.toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q)));
  }, [products, query, catFilter]);

  const cartItems = useMemo(() => Object.entries(cart)
    .map(([pid, qty]) => ({ p: products.find((x) => x.id === pid), qty }))
    .filter((x) => x.p && x.qty > 0), [cart, products]);
  const cartCount = cartItems.reduce((s, x) => s + x.qty, 0);
  const cartTotal = cartItems.reduce((s, x) => s + x.p.price * x.qty, 0);

  const addToCart = (p) => {
    const cur = cart[p.id] || 0;
    if (p.stock > 0 && cur >= p.stock) { toast.error(`Stok ${p.name} hanya tersisa ${p.stock}`); return; }
    setCart((c) => ({ ...c, [p.id]: cur + 1 }));
    toast.success(`${p.name} masuk keranjang`);
  };
  const setQty = (pid, qty, maxStock) => {
    setCart((c) => {
      const n = { ...c };
      const v = Math.max(0, Math.min(qty, maxStock || 99));
      if (v === 0) delete n[pid]; else n[pid] = v;
      return n;
    });
  };

  const checkout = async () => {
    if (!cartItems.length) return;
    setSending(true);
    try {
      const lines = cartItems.map((x) => `- ${x.p.name} x${x.qty} @ ${rupiah(x.p.price)} = ${rupiah(x.p.price * x.qty)}`);
      const note = [...lines, `Total = ${rupiah(cartTotal)}`].join("\n");
      const items = cartItems.map((x) => ({ product_id: x.p.id, name: x.p.name, price: x.p.price, qty: x.qty }));
      const res = await api.post("/tool-requests", { type: "stok_alat", note, items, payment_method: "transfer" });
      toast.success("Pesanan terkirim — pilih rekening tujuan & kirim bukti transfer");
      setCart({}); setCartOpen(false); setTab("pesanan");
      const fresh = await load();
      const created = (fresh || []).find((o) => o.invoice_no === res.data?.invoice_no);
      if (created) openDetail(created);
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const setStatus = async (id, status, ongkir, reason) => {
    try {
      await api.put(`/tool-requests/${id}`, { status, ...(ongkir !== undefined ? { ongkir } : {}), ...(reason ? { reason } : {}) });
      toast.success(`Pengajuan ditandai ${STATUS_LABEL[status]}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const askStatus = (r, status) => {
    if (status === r.status) return;
    setPendingStatus({ id: r.id, status });
    if (status === "diproses") { setOngkirFor(r.id); setOngkirVal(String(r.ongkir || "")); }
    else if (status === "ditolak") { setRejectFor(r.id); setRejectReason(""); }
    else setPinStatusOpen(true);
  };

  const applyStatus = async () => {
    if (!pendingStatus) return;
    await setStatus(
      pendingStatus.id,
      pendingStatus.status,
      pendingStatus.status === "diproses" ? (Number(ongkirVal) || 0) : undefined,
      pendingStatus.status === "ditolak" ? rejectReason.trim() : undefined,
    );
    setPendingStatus(null);
  };

  const pesanUlang = (order) => {
    const nc = {};
    let skipped = 0;
    (order.items || []).forEach((it) => {
      const p = products.find((x) => x.id === it.product_id) || products.find((x) => x.name === it.name);
      if (p && p.stock > 0) nc[p.id] = Math.min(it.qty, p.stock);
      else skipped += 1;
    });
    if (!Object.keys(nc).length) { toast.error("Semua item masih kosong stoknya"); return; }
    setCart(nc); setDetailOrder(null); setTab("belanja"); setCartOpen(true);
    toast.success("Keranjang diisi ulang dari pesanan ini");
    if (skipped) toast.warning(`${skipped} item dilewati (stok kosong)`);
  };

  const openDetail = (r) => { setDetailOrder(r); setPayBank(""); setProofFile(null); setProofPreview(""); };

  const pickProof = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const blob = await compressImage(f);
      setProofFile(new File([blob], "bukti.jpg", { type: "image/jpeg" }));
      setProofPreview(URL.createObjectURL(blob));
    } catch { toast.error("Gagal memproses gambar"); }
  };

  const submitPayment = async () => {
    if (!detailOrder || !proofFile) return;
    const bank = payBank || detailOrder.payment_bank || "";
    if (!bank) { toast.error("Pilih rekening tujuan dulu"); return; }
    setPayBusy(true);
    try {
      const fd = new FormData();
      fd.append("bank", bank);
      fd.append("file", proofFile);
      await api.post(`/tool-requests/${detailOrder.id}/payment`, fd);
      toast.success("Bukti transfer terkirim — menunggu verifikasi admin");
      setProofFile(null); setProofPreview("");
      const fresh = await load();
      const upd = (fresh || []).find((o) => o.id === detailOrder.id);
      if (upd) setDetailOrder(upd);
    } catch (err) { toast.error(errMsg(err)); } finally { setPayBusy(false); }
  };

  const orderDetailDialog = (
    <Dialog open={!!detailOrder} onOpenChange={() => setDetailOrder(null)}>
      <DialogContent data-testid="order-detail-dialog">
        {detailOrder && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-[#F04E23]" /> Detail Pesanan</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-xs max-h-[70vh] overflow-y-auto pr-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-bold text-sm" data-testid="order-invoice-no">{detailOrder.invoice_no}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${STATUS_STYLE[detailOrder.status] || STATUS_STYLE.pending}`} data-testid="order-detail-status">{STATUS_LABEL[detailOrder.status] || detailOrder.status}</span>
              </div>
              <div className="text-muted-foreground font-mono text-[11px]">{fmtDateTime(detailOrder.created_at)}{isAdmin && detailOrder.name ? ` • ${detailOrder.name} (${detailOrder.agent_code || "-"})` : ""}</div>
              <div className="rounded-xl border border-border divide-y divide-border/60">
                {(detailOrder.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`invoice-item-${i}`}>
                    <span className="font-medium">{it.name} <span className="text-muted-foreground font-mono">x{it.qty}</span></span>
                    <span className="font-mono whitespace-nowrap">{rupiah(it.price * it.qty)}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-accent/40 px-3 py-2.5 space-y-1 font-mono">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span data-testid="invoice-subtotal">{rupiah(detailOrder.subtotal || 0)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Ongkir</span><span data-testid="invoice-ongkir">{rupiah(detailOrder.ongkir || 0)}</span></div>
                <div className="flex justify-between font-bold text-sm pt-1 border-t border-border/60"><span>Total Bayar</span><span className="text-[#F04E23]" data-testid="invoice-total">{rupiah(detailOrder.total || 0)}</span></div>
              </div>

              {detailOrder.status === "ditolak" && detailOrder.reject_reason && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-rose-400 font-medium" data-testid="reject-reason-box">
                  Alasan penolakan: {detailOrder.reject_reason}
                </div>
              )}

              {!isAdmin && detailOrder.status === "ditolak" && (detailOrder.items || []).length > 0 && (
                <Button className="w-full" variant="outline" onClick={() => pesanUlang(detailOrder)} data-testid="btn-pesan-ulang">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Pesan Ulang (isi keranjang otomatis)
                </Button>
              )}

              {detailOrder.payment_proof ? (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-2.5 flex items-center gap-2.5" data-testid="payment-proof-box">
                  <img src={`${API_BASE}/tool-requests/files/${detailOrder.payment_proof}`} alt="Bukti transfer" className="w-14 h-14 rounded-lg object-cover cursor-zoom-in" onClick={() => setLightbox({ src: `${API_BASE}/tool-requests/files/${detailOrder.payment_proof}`, alt: "Bukti transfer" })} data-testid="payment-proof-img" />
                  <div className="text-[10px] text-emerald-500 font-semibold">Bukti transfer terkirim ({detailOrder.payment_bank || "bank"}) — menunggu verifikasi admin.</div>
                </div>
              ) : !isAdmin && !["selesai", "ditolak"].includes(detailOrder.status) ? (
                <div className="space-y-2" data-testid="payment-section">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Banknote className="w-3.5 h-3.5 text-emerald-500" /> Pembayaran — Pilih Rekening Tujuan (klik untuk salin)</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {BANK_ACCOUNTS.map((b) => (
                      <button key={b.bank} type="button" onClick={() => { setPayBank(b.bank); try { navigator.clipboard?.writeText(b.number)?.then(() => toast.success(`No. rek ${b.bank} disalin`)).catch(() => {}); } catch {} }} className={`flex items-center justify-between gap-1 rounded-lg border px-2 py-1.5 text-left transition-all active:scale-95 ${(payBank || detailOrder.payment_bank) === b.bank ? "border-[#F04E23] bg-[#F04E23]/10" : "border-border hover:border-[#F04E23]/40"}`} data-testid={`bank-opt-${b.bank}`}>
                        <span className="text-[10px] font-semibold">{b.bank}</span>
                        <span className="text-[9px] font-mono text-muted-foreground flex items-center gap-0.5">{b.number}<Copy className="w-2.5 h-2.5" /></span>
                      </button>
                    ))}
                  </div>
                  <label className="flex items-center gap-2.5 rounded-xl border-2 border-dashed border-border p-2.5 cursor-pointer hover:border-[#F04E23]/50 transition-colors" data-testid="btn-upload-proof">
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
                      {proofPreview ? <img src={proofPreview} className="w-full h-full object-cover" alt="bukti" /> : <ImagePlus className="w-5 h-5 text-muted-foreground" />}
                    </div>
                    <div className="text-[10px] text-muted-foreground">Kirim bukti transfer (foto struk / screenshot). Klik untuk pilih gambar.</div>
                    <input type="file" accept="image/*" className="hidden" onChange={pickProof} data-testid="input-proof" />
                  </label>
                  <Button className="w-full bg-[#F04E23] hover:bg-[#D83E15] text-white" disabled={payBusy || !proofFile || !(payBank || detailOrder.payment_bank)} onClick={submitPayment} data-testid="btn-confirm-payment">
                    {payBusy ? "Mengirim..." : "Konfirmasi Pembayaran"}
                  </Button>
                </div>
              ) : null}

              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Riwayat Pengiriman</div>
                <div data-testid="order-tracking">
                  {(detailOrder.tracking || []).map((t, i, arr) => (
                    <div key={i} className="flex gap-2.5" data-testid={`tracking-${i}`}>
                      <div className="flex flex-col items-center">
                        <span className={`w-2.5 h-2.5 rounded-full mt-1 ${i === arr.length - 1 ? "bg-[#F04E23] shadow shadow-[#F04E23]/40" : "bg-emerald-500"}`} />
                        {i < arr.length - 1 && <span className="w-px flex-1 bg-border min-h-3" />}
                      </div>
                      <div className="pb-3">
                        <div className="font-medium">{t.label}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{fmtDateTime(t.at)}{t.by ? ` • oleh ${t.by}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  if (isAdmin) {
    return (
      <div className="space-y-6" data-testid="pesan-alat-page">
        <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="toolreq-list-card">
          <CardHeader><CardTitle className="text-base font-heading">Semua Pengajuan Masuk</CardTitle></CardHeader>
          <Table className="min-w-[520px]" data-testid="toolreq-table">
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead><TableHead>Agen</TableHead>
                <TableHead>Jenis</TableHead><TableHead>Detail</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!list.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="toolreq-empty">Belum ada pengajuan</TableCell></TableRow>}
              {list.map((r) => (
                <TableRow key={r.id} className={r.invoice_no ? "cursor-pointer hover:bg-accent/40" : ""} onClick={() => r.invoice_no && openDetail(r)} data-testid={`toolreq-row-${r.id}`}>
                  <TableCell className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell><div className="text-xs font-medium">{r.name || r.email}</div><div className="text-[10px] font-mono text-muted-foreground">{r.agent_code || "-"}</div></TableCell>
                  <TableCell><span className="px-2 py-0.5 rounded text-[11px] font-mono bg-sky-500/10 text-sky-500 border border-sky-500/20 whitespace-nowrap">{TYPE_LABEL[r.type] || r.type}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px]"><span className="line-clamp-2">{r.note}</span></TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono border ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`} data-testid={`toolreq-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</span>
                    {r.payment_status === "bukti_terkirim" && <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-mono border bg-amber-500/10 text-amber-500 border-amber-500/30 whitespace-nowrap" data-testid={`bukti-badge-${r.id}`}>Bukti Terkirim</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Select value="" onValueChange={(v) => askStatus(r, v)}>
                      <SelectTrigger className="w-[7.5rem] h-8 text-xs ml-auto" data-testid={`select-status-${r.id}`}>
                        <SelectValue placeholder="Ubah status" />
                      </SelectTrigger>
                      <SelectContent>
                        {["diterima", "diproses", "dikirim", "selesai", "ditolak"].filter((s) => s !== r.status).map((s) => (
                          <SelectItem key={s} value={s} data-testid={`status-opt-${r.id}-${s}`}>{STATUS_LABEL[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={!!ongkirFor} onOpenChange={() => setOngkirFor(null)}>
          <DialogContent data-testid="dialog-ongkir">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-sky-500" /> Proses Pesanan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Ongkos Kirim (Rp)</Label>
                <Input type="number" min="0" value={ongkirVal} onChange={(e) => setOngkirVal(e.target.value)} placeholder="cth: 25000" data-testid="input-ongkir" />
              </div>
              <Button className="w-full" onClick={() => { setOngkirFor(null); setPinStatusOpen(true); }} data-testid="btn-save-ongkir">Lanjut (PIN)</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!rejectFor} onOpenChange={() => setRejectFor(null)}>
          <DialogContent data-testid="dialog-reject">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><XCircle className="w-5 h-5 text-rose-500" /> Tolak Pesanan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Keterangan Penolakan</Label>
                <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="cth: Stok alat kosong" data-testid="input-reject-reason" />
              </div>
              <Button className="w-full" disabled={!rejectReason.trim()} onClick={() => { setRejectFor(null); setPinStatusOpen(true); }} data-testid="btn-reject-next">Lanjut (PIN)</Button>
            </div>
          </DialogContent>
        </Dialog>

        <PinConfirm open={pinStatusOpen} onOpenChange={setPinStatusOpen} title="Ubah Status Pesanan?" description={pendingStatus ? `Status akan diubah menjadi "${STATUS_LABEL[pendingStatus.status]}"${pendingStatus.status === "diproses" ? ` dengan ongkir ${rupiah(Number(ongkirVal) || 0)}` : ""}${pendingStatus.status === "ditolak" ? ` — alasan: ${rejectReason}` : ""}.` : ""} confirmLabel="Ya, Ubah" onConfirm={applyStatus} testid="pin-status-change" />

        {orderDetailDialog}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pesan-alat-page">
      {/* Tab ala marketplace */}
      <div className="inline-flex rounded-full bg-accent/60 p-1 gap-1" data-testid="market-tabs">
        <button onClick={() => setTab("belanja")} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${tab === "belanja" ? "bg-[#F04E23] text-white shadow" : "text-muted-foreground hover:text-foreground"}`} data-testid="tab-belanja">Belanja</button>
        <button onClick={() => setTab("pesanan")} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${tab === "pesanan" ? "bg-[#F04E23] text-white shadow" : "text-muted-foreground hover:text-foreground"}`} data-testid="tab-pesanan">Pesanan Saya{list.length ? ` (${list.length})` : ""}</button>
      </div>

      {tab === "belanja" ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari produk..." className="pl-10 rounded-full h-10 bg-card" data-testid="input-search-product" />
            </div>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-40 h-10 rounded-full" data-testid="select-product-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c} data-testid={`cat-opt-${c}`}>{c === "ALL" ? "Semua Kategori" : c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {!filtered.length && <div className="text-center py-10 text-sm text-muted-foreground" data-testid="product-market-empty">Produk tidak ditemukan.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="product-market">
            {filtered.map((p) => {
              const inCart = cart[p.id] || 0;
              const habis = !(p.stock > 0);
              return (
                <div key={p.id} className="rounded-2xl border border-border bg-card overflow-hidden group" data-testid={`market-product-${p.id}`}>
                  <div className="aspect-[4/3] bg-muted flex items-center justify-center overflow-hidden cursor-zoom-in" onClick={() => p.image_path && setLightbox({ src: `${API_BASE}/products/files/${p.image_path}`, alt: p.name })} data-testid={`market-product-img-${p.id}`}>
                    {p.image_path ? <img src={`${API_BASE}/products/files/${p.image_path}`} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <Package className="w-8 h-8 text-muted-foreground/40" />}
                  </div>
                  <div className="p-2 space-y-1">
                    <div className="text-[11px] font-semibold leading-snug line-clamp-2 min-h-[1.9rem]">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{p.category || "Tanpa kategori"}</div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono font-bold text-[#F04E23]">{rupiah(p.price)}</span>
                      <span className={`text-[10px] font-mono ${habis ? "text-rose-500 font-bold" : "text-muted-foreground"}`}>{habis ? "Habis" : `stok:${p.stock}`}</span>
                    </div>
                    <Button type="button" size="sm" className="w-full h-7 text-[11px] bg-[#F04E23] hover:bg-[#D83E15] text-white active:scale-95 transition-all" disabled={habis} onClick={() => addToCart(p)} data-testid={`btn-beli-${p.id}`}>
                      <ShoppingCart className="w-3 h-3 mr-1" /> {inCart ? `+keranjang (${inCart})` : "beli"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="space-y-2.5" data-testid="order-history">
          {!list.length && <div className="text-center py-10 text-sm text-muted-foreground" data-testid="order-empty">Belum ada pesanan — belanja dulu di tab Belanja.</div>}
          {list.map((r) => (
            <div key={r.id} className={`rounded-2xl border border-border bg-card p-3.5 ${r.invoice_no ? "cursor-pointer hover:border-[#F04E23]/40 hover:shadow-md active:scale-[0.99] transition-all" : ""}`} onClick={() => r.invoice_no && openDetail(r)} data-testid={`order-card-${r.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                  <Package className="w-3.5 h-3.5" /> {fmtDateTime(r.created_at)}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`} data-testid={`toolreq-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</span>
              </div>
              {r.invoice_no && <div className="text-[10px] font-mono text-muted-foreground mb-1.5">{r.invoice_no}</div>}
              {r.status === "ditolak" && r.reject_reason && <div className="text-[10px] text-rose-400 font-mono mb-1.5" data-testid={`order-reject-${r.id}`}>Ditolak: {r.reject_reason}</div>}
              <div className="space-y-0.5">
                {r.invoice_no
                  ? (r.items || []).slice(0, 2).map((it, i) => (
                    <div key={i} className="text-[11px] font-mono text-muted-foreground">- {it.name} x{it.qty}</div>
                  ))
                  : (r.note || "").split("\n").filter(Boolean).map((line, i) => (
                    <div key={i} className={`text-[11px] font-mono ${line.startsWith("Total") ? "font-bold text-foreground pt-1 border-t border-border/60 mt-1" : "text-muted-foreground"}`}>{line}</div>
                  ))}
                {r.invoice_no && (r.items || []).length > 2 && <div className="text-[10px] text-muted-foreground italic">+{(r.items || []).length - 2} item lainnya</div>}
              </div>
              {r.invoice_no ? (
                <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-border/60">
                  <span className="text-[10px] text-muted-foreground">Total Bayar{detailOrder ? "" : " (klik untuk invoice)"}</span>
                  <span className="font-mono font-extrabold text-[#F04E23] text-xs">{rupiah(r.total || 0)}</span>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {orderDetailDialog}

      {/* Tombol keranjang melayang */}
      <button onClick={() => setCartOpen(true)} className="fixed bottom-40 lg:bottom-24 right-4 lg:right-6 z-40 w-12 h-12 rounded-full bg-[#F04E23] hover:bg-[#D83E15] text-white flex items-center justify-center shadow-lg shadow-[#F04E23]/30 active:scale-95 transition-all" data-testid="btn-cart" title="Keranjang belanja">
        <ShoppingCart className="w-5 h-5" />
        {cartCount > 0 && <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-white text-[#F04E23] text-[10px] font-bold flex items-center justify-center border border-[#F04E23]/30" data-testid="cart-badge">{cartCount}</span>}
      </button>

      {/* Dialog keranjang */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent data-testid="cart-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-[#F04E23]" /> Keranjang ({cartCount})</DialogTitle></DialogHeader>
          {!cartItems.length && <div className="text-center py-8 text-sm text-muted-foreground" data-testid="cart-empty">Keranjang kosong — klik "beli" pada produk.</div>}
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {cartItems.map(({ p, qty }) => (
              <div key={p.id} className="flex items-center gap-2.5 rounded-xl border border-border p-2" data-testid={`cart-item-${p.id}`}>
                <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                  {p.image_path ? <img src={`${API_BASE}/products/files/${p.image_path}`} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-muted-foreground/40" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold truncate">{p.name}</div>
                  <div className="text-[10px] font-mono text-[#F04E23] font-bold">{rupiah(p.price)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setQty(p.id, qty - 1, p.stock)} data-testid={`btn-cart-minus-${p.id}`}><Minus className="w-3 h-3" /></Button>
                  <span className="w-6 text-center text-xs font-mono font-bold" data-testid={`cart-qty-${p.id}`}>{qty}</span>
                  <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setQty(p.id, qty + 1, p.stock)} data-testid={`btn-cart-plus-${p.id}`}><Plus className="w-3 h-3" /></Button>
                  <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-rose-500" onClick={() => setQty(p.id, 0)} data-testid={`btn-cart-del-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
          {cartItems.length > 0 && (
            <>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground">Total</span>
                <span className="font-mono font-extrabold text-[#F04E23]" data-testid="cart-total">{rupiah(cartTotal)}</span>
              </div>
              <Button className="w-full bg-[#F04E23] hover:bg-[#D83E15] text-white active:scale-[0.98] transition-all" onClick={checkout} disabled={sending} data-testid="btn-checkout">
                {sending ? "Mengirim..." : `Checkout — ${rupiah(cartTotal)}`}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
      {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </div>
  );
}
